import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'summarise',
  version: '1.0.0',
  properties: [
    // ═══════════════════════════════════════════════════════════════
    // INPUT ARRAY SELECTION
    // ═══════════════════════════════════════════════════════════════
    {
      key: 'inputArray',
      label: 'Input Array',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'The array to summarise. Drag from the input panel or type the path.',
    },

    // ═══════════════════════════════════════════════════════════════
    // GROUP BY FIELDS (Multiple Selection)
    // ═══════════════════════════════════════════════════════════════
    {
      key: 'groupByFields',
      label: 'Group By Fields',
      type: 'fixedCollection',
      required: false,
      typeOptions: {
        multipleValues: true,
      },
      default: {
        values: [],
      },
      description: 'Fields to group by (like SQL GROUP BY). Leave empty to summarise all items into one result.',
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Group Field',
          description: 'Add fields to group by',
          values: [
            {
              key: 'field',
              label: 'Field',
              type: 'options',
              required: true,
              placeholder: 'Select a field',
              description: 'The field to group by',
              options: [],
              typeOptions: {
                loadOptionsFrom: 'inputArray',
              },
            },
          ],
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════════
    // AGGREGATIONS (Fields to Summarise)
    // ═══════════════════════════════════════════════════════════════
    {
      key: 'aggregations',
      label: 'Fields to Summarise',
      type: 'fixedCollection',
      required: true,
      typeOptions: {
        multipleValues: true,
      },
      default: {
        values: [],
      },
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Aggregation',
          description: 'Define how to aggregate each field',
          values: [
            {
              key: 'field',
              label: 'Field',
              type: 'options',
              required: true,
              placeholder: 'Select a field',
              description: 'The field to aggregate',
              options: [],
              typeOptions: {
                loadOptionsFrom: 'inputArray',
              },
            },
            {
              key: 'operation',
              label: 'Operation',
              type: 'options',
              required: true,
              default: 'sum',
              options: [
                { value: 'sum', name: 'Sum' },
                { value: 'average', name: 'Average' },
                { value: 'count', name: 'Count' },
                { value: 'countUnique', name: 'Count Unique' },
                { value: 'min', name: 'Minimum' },
                { value: 'max', name: 'Maximum' },
                { value: 'first', name: 'First Value' },
                { value: 'last', name: 'Last Value' },
                { value: 'concat', name: 'Concatenate' },
                { value: 'collect', name: 'Collect All Values' },
              ],
            },
            {
              key: 'outputField',
              label: 'Output Field Name',
              type: 'string',
              required: false,
              placeholder: 'e.g., totalAmount',
              description: 'Custom name for the result field. Defaults to "{field}_{operation}".',
            },
            {
              key: 'separator',
              label: 'Separator',
              type: 'string',
              default: ', ',
              placeholder: ', ',
              description: 'Separator for concatenation',
              displayOptions: {
                show: {
                  operation: ['concat'],
                },
              },
            },
          ],
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════════
    // OUTPUT OPTIONS
    // ═══════════════════════════════════════════════════════════════
    {
      key: 'outputFormat',
      label: 'Output Format',
      type: 'options',
      default: 'array',
      options: [
        { value: 'array', name: 'Separate Items (Array)' },
        { value: 'object', name: 'Single Object (Grouped)' },
      ],
      description: 'How to structure the output. Array returns one item per group; Object returns nested structure.',
    },
    {
      key: 'includeCount',
      label: 'Include Item Count',
      type: 'boolean',
      default: true,
      description: 'Add a _count field showing how many items were in each group',
    },
    {
      key: 'skipEmptyGroups',
      label: 'Skip Empty Groups',
      type: 'boolean',
      default: true,
      description: 'Exclude groups where the group-by field is null or empty',
    },
  ],
};

export default schema;
